import React from 'react';
import { Box, Typography } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';

/**
 * LineChartWidget - Renders a line chart for time-series data
 * 
 * @param {Array} data - Array of data points with xLabel and yValue
 * @param {Object} config - Widget configuration (title, description, etc.)
 */
export default function LineChartWidget({ data, config, height = 300 }) {
  // Aggregate data by date (xLabel or dateRef)
  const aggregatedData = data.reduce((acc, item) => {
    // Prefer dateRef if available for sorting/grouping, fallback to xLabel (which might be company name in raw data)
    // But for time series, we expect dateRef to be the key.
    // However, the current data might have xLabel as company.
    // Let's use dateRef if it exists, otherwise xLabel
    const key = item.dateRef || item.xLabel; 
    
    if (!key) return acc;

    const value = parseFloat(item.yValue || 0);

    if (!acc[key]) {
      acc[key] = { xLabel: key, yValue: 0 };
    }
    acc[key].yValue += value;
    return acc;
  }, {});

  // Convert to array and sort by date
  const sortedData = Object.values(aggregatedData).sort((a, b) => {
    return new Date(a.xLabel) - new Date(b.xLabel);
  });

  // Transform data for MUI X-Charts
  const xLabels = sortedData.map(item => item.xLabel);
  const yValues = sortedData.map(item => parseFloat(item.yValue));

  // If only one data point, show as a big number instead of a line chart
  if (data.length === 1) {
    return (
      <Box 
        height={height} 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center"
      >
        <Typography variant="h2" color="primary.main" fontWeight="bold">
          {yValues[0].toLocaleString()}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {xLabels[0]}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <LineChart
        xAxis={[{ 
          scaleType: 'point', 
          data: xLabels 
        }]}
        slotProps={{ legend: { hidden: true } }}
        series={[
          {
            data: yValues,
            label: config.title,
            color: '#1976d2',
            curve: 'linear',
          },
        ]}
        height={height}
        margin={{ top: 10, right: 10, bottom: 30, left: 50 }}
      />
    </Box>
  );
}
